import { Box, Dialog, IconButton, Slide, Tab, Tabs, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import type { TransitionProps } from "@mui/material/transitions";
import { forwardRef, useEffect, useMemo, useState } from "react";
import StickyActionBar from "@/components/mobile/StickyActionBar";
import { useIsMobile } from "@/hooks/useIsMobile";
import InvoicePdfViewer from "../components/InvoicePdfViewer";
import ValidationChecksCard from "../components/ValidationChecksCard";
import ExtractionResultCard from "../components/ExtractionResultCard";
import SystemFailureAlert from "../components/SystemFailureAlert";
import { V2_COLORS } from "../constants/colors";
import type { ValidationCheck } from "../types/action";
import { LIFECYCLE_STATUS, type LifecycleStatus } from "../types/common";
import type { ParseResultDetail } from "../types/invoice";
import type { SystemFailureNotice } from "../utils/systemFailure";

const SlideUp = forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type RightTab = "extraction" | "validation";

// Hoisted out of render so Emotion doesn't re-process these on every keystroke.
const RIGHT_PANE_SX = { overflow: "hidden", minHeight: 0 } as const;
// Desktop: 50-50 side-by-side. Mobile: PDF stacked above the tabs pane.
const PDF_PANE_SX = {
  flex: 1,
  overflow: "hidden",
  p: 2,
  borderRight: `1px solid ${V2_COLORS.border}`,
} as const;
const PDF_PANE_SX_MOBILE = {
  height: "40vh",
  flexShrink: 0,
  overflow: "hidden",
  p: 1,
  borderBottom: `1px solid ${V2_COLORS.border}`,
} as const;
const TABS_HEADER_SX = {
  borderBottom: `1px solid ${V2_COLORS.border}`,
  flexShrink: 0,
  px: 2,
  pt: 1,
};
const TABS_SX = {
  minHeight: 36,
  "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontWeight: 600 },
};
const TABS_PANEL_SX = { overflow: "auto", p: 2, flex: 1, minHeight: 0 };

interface InvoiceReviewModalProps {
  open: boolean;
  onClose: () => void;
  invoiceNo: string;
  invoiceImage: { file_name: string; pre_signed_url: string } | null;
  checks: ValidationCheck[];
  /**
   * Lifecycle status determines the default tab when both extraction and
   * validation data are present. OCR_FAILED → Extraction first; otherwise →
   * Validation first (the user usually cares about validation outcomes once
   * OCR has succeeded).
   */
  lifecycleStatus?: LifecycleStatus;
  parseResponse?: ParseResultDetail | null;
  /**
   * Action buttons (same as detail page header). Desktop: rendered in the modal
   * header top-right. Mobile: rendered in a sticky bottom bar instead.
   */
  actions?: React.ReactNode;
  /** System failure (e.g. OCR_FAILED reason), shown above the right pane. */
  systemFailure?: SystemFailureNotice | null;
}

export default function InvoiceReviewModal({
  open,
  onClose,
  invoiceNo,
  invoiceImage,
  checks,
  lifecycleStatus,
  parseResponse,
  actions,
  systemFailure,
}: InvoiceReviewModalProps) {
  const hasExtraction = !!parseResponse;
  const hasValidation = checks.length > 0;
  const isOcrFailed = lifecycleStatus === LIFECYCLE_STATUS.OCR_FAILED;
  const isMobile = useIsMobile();

  // Pick a default tab once we know what's available. When OCR has failed we
  // always lead with Extraction; otherwise Validation if it has data, else
  // Extraction. Falls back to "extraction" so the right pane never renders
  // a tab that has no panel.
  const defaultTab: RightTab = useMemo(() => {
    if (isOcrFailed && hasExtraction) return "extraction";
    if (hasValidation) return "validation";
    if (hasExtraction) return "extraction";
    return "extraction";
  }, [isOcrFailed, hasExtraction, hasValidation]);

  const [activeTab, setActiveTab] = useState<RightTab>(defaultTab);

  // Reset to the computed default each time the modal opens so a stale
  // selection from a previous invoice doesn't carry over.
  useEffect(() => {
    if (open) setActiveTab(defaultTab);
  }, [open, defaultTab]);

  const showTabs = hasExtraction && hasValidation;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={SlideUp}
      PaperProps={{ sx: { bgcolor: "#F5F5F5" } }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        px={2.5}
        py={1.5}
        sx={{
          bgcolor: "background.paper",
          borderBottom: `1px solid ${V2_COLORS.border}`,
          flexShrink: 0,
        }}
      >
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
        <Box>
          <Typography fontSize="12px" color={V2_COLORS.textSecondary}>
            Invoice {invoiceNo}
          </Typography>
          <Typography fontSize="16px" fontWeight={600}>
            Review Invoice
          </Typography>
        </Box>
        {!isMobile && actions && (
          <>
            <Box sx={{ flexGrow: 1 }} />
            <Box display="flex" gap={1.5} alignItems="center">
              {actions}
            </Box>
          </>
        )}
      </Box>

      {/* Body: 50-50 split on desktop, PDF stacked above tabs on mobile */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        flex={1}
        overflow="hidden"
        minHeight={0}
      >
        {/* PDF viewer */}
        <Box display="flex" flexDirection="column" sx={isMobile ? PDF_PANE_SX_MOBILE : PDF_PANE_SX}>
          {invoiceImage?.pre_signed_url ? (
            <InvoicePdfViewer
              url={invoiceImage.pre_signed_url}
              fileName={invoiceImage.file_name}
              showHeader={false}
              fillHeight
            />
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <Typography color="textSecondary">No invoice document available</Typography>
            </Box>
          )}
        </Box>

        {/* Extraction / validation. Shows tabs only when both have data. */}
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          sx={RIGHT_PANE_SX}
        >
          <SystemFailureAlert notice={systemFailure} sx={{ m: 2, mb: 0, flexShrink: 0 }} />
          {showTabs && (
            <Box sx={TABS_HEADER_SX}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v as RightTab)}
                textColor="primary"
                indicatorColor="primary"
                sx={TABS_SX}
              >
                <Tab value="extraction" label="Extraction" />
                <Tab value="validation" label="Validation" />
              </Tabs>
            </Box>
          )}
          <Box sx={TABS_PANEL_SX}>
            {showTabs ? (
              activeTab === "extraction" ? (
                <ExtractionResultCard parseResponse={parseResponse ?? null} />
              ) : (
                <ValidationChecksCard checks={checks} />
              )
            ) : hasExtraction ? (
              <ExtractionResultCard parseResponse={parseResponse ?? null} />
            ) : (
              <ValidationChecksCard checks={checks} />
            )}
          </Box>
        </Box>
      </Box>

      {/* Mobile: actions in a sticky bottom bar (header has no room for them) */}
      {isMobile && actions && <StickyActionBar>{actions}</StickyActionBar>}
    </Dialog>
  );
}
